import React from "react";
import { NavLink, Link } from "react-router-dom";
import { UserMenu } from "@/components/UserMenu";
import { StandardQuestionDeleteMenu } from "@/components/StandardQuestionDeleteMenu";
import { getInterviewReturnTarget } from "@/lib/activeInterviewNavigation";

/*
 * Shared top-bar for every admin page. Provides quick section navigation
 * (Questions / Beta Codes / Users / Audit Log), a direct "Launch app" jump-off
 * to the interview simulator, and a link back to the user app.
 *
 * NOTE ON CREDITS: When credit-consumption is wired to interview sessions
 * later, admins must bypass the credit check entirely (unlimited access).
 * That check lives on the backend session-creation endpoint — not here.
 */

const TABS = [
  { to: "/admin", label: "Dashboard", testId: "admin-nav-home", exact: true },
  { to: "/admin/standard-questions", label: "Standard Questions", testId: "admin-nav-standard-questions" },
  { to: "/admin/responsive-preview", label: "Responsive Preview", testId: "admin-nav-responsive-preview" },
  { to: "/admin/beta-codes", label: "Beta Codes", testId: "admin-nav-beta-codes" },
  { to: "/admin/beta-reports", label: "Beta Reports", testId: "admin-nav-beta-reports" },
  { to: "/admin/beta-report-wording", label: "Beta Report Wording", testId: "admin-nav-beta-report-wording" },
  // The earlier 8-section form; past submissions only (replaced by the Beta Report 2026-10-30).
  { to: "/admin/beta-feedback", label: "Old Beta Feedback", testId: "admin-nav-beta-feedback" },
  { to: "/admin/users", label: "Users", testId: "admin-nav-users" },
  { to: "/admin/prep-failures", label: "Prep Failures", testId: "admin-nav-prep-failures" },
  { to: "/admin/audit", label: "Audit Log", testId: "admin-nav-audit" },
  { to: "/admin/bulk-audit", label: "Bulk Audit", testId: "admin-nav-bulk-audit" },
  { to: "/admin/privacy-audit", label: "Privacy Audit", testId: "admin-nav-privacy-audit" },
  { to: "/admin/voice-check", label: "Voice Check", testId: "admin-nav-voice-check" },
  { to: "/admin/localization-audit", label: "Localization Audit", testId: "admin-nav-localization-audit" },
];

export function AdminNav({ title }) {
  const isStandardQuestions = title === "Standard Interview Questions";

  return (
    <header
      className="border-b border-[color:var(--border-stone)] bg-white"
      data-testid="admin-nav"
    >
      <div className="flex items-center justify-between px-6 py-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">
            English Job Interview Simulator · Admin
          </div>
          <h1 className="text-xl font-semibold text-[color:var(--text-primary)]">
            {title}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {isStandardQuestions && <StandardQuestionDeleteMenu />}
          <Link
            to={getInterviewReturnTarget("/")}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-4 py-1.5 text-xs font-medium"
            data-testid="admin-launch-app"
          >
            Launch App →
          </Link>
          <UserMenu />
        </div>
      </div>
      <nav className="flex flex-wrap gap-1 px-6 pb-2" role="tablist">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.exact}
            data-testid={t.testId}
            className={({ isActive }) =>
              [
                "px-3 py-1.5 rounded-full text-xs font-medium tracking-normal border",
                isActive
                  ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]"
                  : "border-[color:var(--border-stone)] text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]",
              ].join(" ")
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
